import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { getUser, usersQueryKeys } from '../../api/users';
import {
  USERS_SEARCH_DEFAULTS,
  parseUsersSearch,
} from '../../hooks/useUsersRouterSearch';
import { Button } from '@/components/ui/button';
import { Page } from '../../components/layouts/Page';
import { ResponsivePageHeader } from '../../components/layouts/ResponsivePageHeader';
import { openMobileSidebar } from '../../components/MobileSidebar/mobileSidebarEvents';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BREADCRUMB_LIST_APP_CLASS,
} from '../../components/ui/breadcrumb';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES, can } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
import { canEditUser } from '../../components/UserManagement/canEditUser';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type UserManagement from '../../components/UserManagement/index';
import type UserDetails from '../../components/UserDetails/index';
import type EditUser from '../../components/EditUser/index';
import type { User } from '../../api/users';

export const USER_STALE_TIME_MS = 30_000;

// Seeded from the Phlex view when the URL is the record's own, so the page
// paints without a round-trip. A later edit route shares this cache key.
export function useUserRaw(idNum: number, seed: User | undefined) {
  return useQuery({
    queryKey: usersQueryKeys.detail(idNum),
    queryFn: () => getUser(idNum),
    staleTime: USER_STALE_TIME_MS,
    ...(seed ? { initialData: seed, initialDataUpdatedAt: Date.now() } : {}),
  });
}

export const usersIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  validateSearch: parseUsersSearch,
  search: { middlewares: [stripSearchParams(USERS_SEARCH_DEFAULTS)] },
  component: function UsersIndexRoute() {
    const { initialUsers, currentUser, slingPositions = [] } = useAppRouterInitial();
    const { t } = useTranslation();
    // First render renders the skeleton so React hydration byte-matches the
    // Phlex skeleton; the real component is dynamic-imported in an effect so
    // it stays in its own chunk.
    useDocumentTitle(t('common.page_titles.users'));
    const [Component, setComponent] = useState<typeof UserManagement | null>(null);
    useEffect(() => {
      import('../../components/UserManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.page_titles.users')} />;
    }
    return <Component initialUsers={initialUsers} currentUser={currentUser} slingPositions={slingPositions} />;
  },
});

export const userDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$id/details',
  component: function UserDetailsRoute() {
    const params = userDetailsRoute.useParams();
    const { initialUser, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed = initialUser && initialUser.id === idNum ? initialUser : undefined;
    const { data } = useUserRaw(idNum, seed);
    useDocumentTitle(data?.name ?? t('common.page_titles.users'));
    const [Component, setComponent] = useState<typeof UserDetails | null>(null);
    useEffect(() => {
      import('../../components/UserDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <DetailsSkeleton label={t('page_title')} />;
    }
    return <Component user={data} currentUser={currentUser} />;
  },
});

export const userEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$id/edit',
  // Actor-only: this runs before the record is known. The system-account rule is below.
  beforeLoad: requireCapability(CAPABILITIES.USERS_WRITE, ({ params }) => ({
    to: '/users/$id/details',
    params: { id: params.id },
  })),
  component: function UserEditRoute() {
    const params = userEditRoute.useParams();
    const { initialUser, currentUser, slingPositions = [] } = useAppRouterInitial();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const idNum = Number(params.id);
    const seed = initialUser && initialUser.id === idNum ? initialUser : undefined;
    const { data, isError, refetch } = useUserRaw(idNum, seed);
    useDocumentTitle(
      data ? t('common.page_titles.edit', { name: data.name ?? data.email }) : t('common.page_titles.users'),
    );
    const [Component, setComponent] = useState<typeof EditUser | null>(null);
    useEffect(() => {
      import('../../components/EditUser/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);

    // System accounts and users outside the actor's role scope stay readable — send them to the detail page.
    const isLocked = data !== undefined && !canEditUser(currentUser, data);
    useEffect(() => {
      if (!isLocked) return;
      navigate({ to: '/users/$id/details', params: { id: params.id }, replace: true });
    }, [isLocked, navigate, params.id]);

    // Initial-load failure only; a failed background refetch keeps the form mounted.
    if (isError && !data) {
      return <UserEditLoadError onRetry={() => refetch()} />;
    }

    if (!Component || !data || isLocked) {
      return <EditSkeleton label={t('page_title')} />;
    }

    return (
      <Component
        user={data}
        canWrite={can(currentUser, CAPABILITIES.USERS_WRITE)}
        currentUser={currentUser}
        slingPositions={slingPositions}
      />
    );
  },
});

// Shown only when the record never loaded.
function UserEditLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();

  return (
    <Page>
      <Page.Header className="py-6 md:py-0">
        <ResponsivePageHeader
          mobile={{
            title: t('page_title'),
            openNavigationAriaLabel: t('header.open_navigation'),
            onOpenNavigation: openMobileSidebar,
          }}
        >
          <Breadcrumb>
            <BreadcrumbList className={BREADCRUMB_LIST_APP_CLASS}>
              <BreadcrumbItem>
                <BreadcrumbLink to="/users" search={USERS_SEARCH_DEFAULTS}>
                  {t('common.page_titles.users')}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </ResponsivePageHeader>
      </Page.Header>
      <Page.Body>
        <div className="w-full space-y-4 px-6 sm:px-8 lg:px-16">
          <p role="alert" className="font-inter text-sm text-chilli-red">
            {t('edit.error')}
          </p>
          <Button type="button" variant="outline" onClick={onRetry}>
            {t('edit.retry')}
          </Button>
        </div>
      </Page.Body>
    </Page>
  );
}
